import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import Chart from 'react-apexcharts';

const DEFAULT_COLORS = ['#007DC3', '#00A896', '#F77F00', '#D62828', '#6A4C93', '#1982C4', '#8AC926'];

const ChartRenderer = ({
  chartType = 'bar', // 'line', 'area', 'bar', 'scatter', 'pie', 'radialbar'
  chartData = [],
  xAxisKey = 'name',
  yAxisKey = null, // For single series
  seriesKeys = [], // For multiple series
  xAxisLabel = '',
  yAxisLabel = '',
  colors = null, // Optional custom colors array
  legendKey = null,
  chartId = 'chart',
  margin = { top: 20, right: 30, left: 30, bottom: 60 },
  height = 450,
  showLegend = true,
  // For pie chart specific
  pieDataKey = 'value',
  pieNameKey = 'name',
  // For bar/line chart specific
  dataKey = null, // Used in some cases for bar/line
  allSeries = [], // Used in some cases for bar/line
  strokeWidth = 2,
  // Styling options
  gridStroke = '#e8eaed',
  xAxisTickFontSize = 12,
  yAxisTickFontSize = 11,
  xAxisLabelStyle = null,
  yAxisLabelStyle = null,
  stacked = false,
  horizontal = false,
  averageLine = null,
  barColors = null,
}) => {
  // Ensure colors is always an array
  const chartColors = Array.isArray(barColors) && barColors.length > 0
    ? barColors
    : (Array.isArray(colors) && colors.length > 0 ? colors : DEFAULT_COLORS);
  const useDistributedBars = (chartType === 'bar' || chartType === 'column') &&
    Array.isArray(barColors) && barColors.length > 0;
  const finalSeriesKeys = seriesKeys.length > 0 ? seriesKeys : (yAxisKey ? [yAxisKey] : []);
  const showLegendCondition = showLegend && (finalSeriesKeys.length > 1 || legendKey);

  // Prepare pie chart data (always calculate, even if not used)
  const pieData = useMemo(() => {
    if (!chartData || chartData.length === 0) return [];
    return chartData.map(item => ({
      name: item[pieNameKey] || item.name || 'Unknown',
      value: item[pieDataKey] || item.value || 0
    }));
  }, [chartData, pieNameKey, pieDataKey]);

  const pieSeries = useMemo(() => pieData.map(item => item.value || 0), [pieData]);

  // Prepare bar/line chart data (always calculate, even if not used)
  const categories = useMemo(() => {
    if (!chartData || chartData.length === 0 || !xAxisKey) return [];
    const cats = chartData.map(item => {
      const value = item[xAxisKey];
      return value !== null && value !== undefined ? String(value) : '';
    }).filter(cat => cat !== '');
    return cats;
  }, [chartData, xAxisKey]);

  const series = useMemo(() => {
    if (!chartData || chartData.length === 0) return [];
    
    if (allSeries.length > 0) {
      // Use allSeries if provided
      return allSeries.map((seriesItem, idx) => {
        const seriesData = chartData.map(item => {
          if (!item) return 0;
          const value = item[seriesItem.name] || item[seriesItem.dataKey];
          // Convert null/undefined to 0 for bar/column charts
          if (value === null || value === undefined) return 0;
          const numValue = typeof value === 'number' ? value : parseFloat(value);
          return isNaN(numValue) ? 0 : numValue;
        });
        return {
          name: seriesItem.name || `Series ${idx + 1}`,
          data: seriesData
        };
      });
    } else if (finalSeriesKeys.length > 0) {
      // Use seriesKeys
      return finalSeriesKeys.map((key, index) => {
        const seriesData = chartData.map(item => {
          if (!item) return 0;
          const value = item[key];
          // Convert null/undefined to 0 for bar/column charts
          if (value === null || value === undefined) return 0;
          const numValue = typeof value === 'number' ? value : parseFloat(value);
          return isNaN(numValue) ? 0 : numValue;
        });
        return {
          name: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          data: seriesData
        };
      });
    }
    return [];
  }, [chartData, allSeries, finalSeriesKeys]);

  const memoizedSeries = useMemo(() => {
    const validSeries = series.filter(s => s && s.data && Array.isArray(s.data) && s.data.length > 0);
    if (validSeries.length === 0) return [];
    
    // Ensure all series have the same length as categories
    const maxDataLength = Math.max(...validSeries.map(s => s.data.length), categories.length);
    
    return validSeries.map(s => ({
      name: s.name,
      data: Array.from({ length: maxDataLength }, (_, idx) => {
        const value = s.data[idx];
        // Convert null/undefined to 0 - critical for bar/column charts
        if (value === null || value === undefined) return 0;
        const numValue = typeof value === 'number' ? value : parseFloat(value);
        return isNaN(numValue) ? 0 : numValue;
      })
    }));
  }, [series, categories]);

  // Calculate maxLength for normalization (always calculate, even if not used)
  const maxLength = useMemo(() => {
    if (!categories || categories.length === 0 || !memoizedSeries || memoizedSeries.length === 0) {
      return 0;
    }
    const seriesMaxLength = Math.max(...memoizedSeries.map(s => (s && s.data && Array.isArray(s.data)) ? s.data.length : 0), 0);
    const catLength = categories.length;
    return Math.max(catLength, seriesMaxLength);
  }, [categories, memoizedSeries]);

  // Normalize categories (always calculate, even if not used)
  const normalizedCategories = useMemo(() => {
    if (maxLength === 0) return [];
    return Array.from({ length: maxLength }, (_, idx) => 
      (categories && categories[idx]) ? String(categories[idx]) : `Category ${idx + 1}`
    );
  }, [categories, maxLength]);

  // Normalize series (always calculate, even if not used)
  const normalizedSeries = useMemo(() => {
    if (!memoizedSeries || memoizedSeries.length === 0 || maxLength === 0) return [];
    return memoizedSeries.map(s => ({
      ...s,
      data: Array.from({ length: maxLength }, (_, idx) => {
        const value = s.data && s.data[idx] !== undefined ? s.data[idx] : 0;
        // Convert null/undefined to 0 - critical for bar/column charts
        if (value === null || value === undefined) return 0;
        const numValue = typeof value === 'number' ? value : parseFloat(value);
        return isNaN(numValue) ? 0 : numValue;
      })
    }));
  }, [memoizedSeries, maxLength]);

  // Final series with length matching (always calculate, even if not used)
  const finalSeries = useMemo(() => {
    if (!normalizedSeries || normalizedSeries.length === 0 || !normalizedCategories || normalizedCategories.length === 0) {
      return [];
    }
    return normalizedSeries.map(s => {
      if (!s || !s.data || !Array.isArray(s.data)) {
        return { 
          name: s?.name || 'Series', 
          data: Array(normalizedCategories.length).fill(0) 
        };
      }
      if (s.data.length !== normalizedCategories.length) {
        // Fix length mismatch by padding or truncating
        const fixedData = Array.from({ length: normalizedCategories.length }, (_, idx) => {
          const value = s.data[idx];
          if (value === null || value === undefined) return 0;
          const numValue = typeof value === 'number' ? value : parseFloat(value);
          return isNaN(numValue) ? 0 : numValue;
        });
        return { ...s, data: fixedData };
      }
      // Ensure all values are numbers
      const validatedData = s.data.map(v => {
        if (v === null || v === undefined) return 0;
        const numValue = typeof v === 'number' ? v : parseFloat(v);
        return isNaN(numValue) ? 0 : numValue;
      });
      return { ...s, data: validatedData };
    });
  }, [normalizedSeries, normalizedCategories]);

  // Radial bar chart options
  const radialBarOptions = useMemo(() => ({
    chart: {
      type: 'radialBar',
      height: height,
      toolbar: {
        show: false
      }
    },
    plotOptions: {
      radialBar: {
        dataLabels: {
          name: {
            fontSize: '13px'
          },
          value: {
            fontSize: '16px',
            fontWeight: 600
          },
          total: {
            show: true,
            label: 'Total',
            formatter: function (w) {
              return w.globals.seriesTotals.reduce((a, b) => a + b, 0).toLocaleString();
            }
          }
        }
      }
    },
    labels: pieData.map(item => item.name),
    colors: chartColors,
    legend: {
      show: showLegend,
      position: 'bottom',
      fontSize: '13px',
      fontFamily: 'inherit',
      fontWeight: 400,
      offsetY: 0,
      itemMargin: {
        horizontal: 10,
        vertical: 5
      }
    },
    tooltip: {
      y: {
        formatter: function (val) {
          return val ? val.toLocaleString() : '0';
        }
      }
    }
  }), [height, chartColors, showLegend, pieData]);

  // Pie chart options
  const pieOptions = useMemo(() => ({
    chart: {
      type: 'pie',
      height: height,
      toolbar: {
        show: false
      }
    },
    labels: pieData.map(item => item.name),
    colors: chartColors,
    legend: {
      show: showLegend,
      position: 'bottom',
      fontSize: '13px',
      fontFamily: 'inherit',
      fontWeight: 400,
      offsetY: 0,
      itemMargin: {
        horizontal: 10,
        vertical: 5
      }
    },
    tooltip: {
      y: {
        formatter: function (val) {
          return val ? val.toLocaleString() : '0';
        }
      }
    },
    dataLabels: {
      enabled: true,
      formatter: function (val, opts) {
        if (opts && opts.w && opts.w.config && opts.w.config.series && opts.w.config.series[opts.seriesIndex] !== undefined) {
          return opts.w.config.series[opts.seriesIndex].toLocaleString();
        }
        return val ? val.toLocaleString() : '0';
      }
    },
    responsive: [{
      breakpoint: 480,
      options: {
        chart: {
          width: 200
        },
        legend: {
          position: 'bottom'
        }
      }
    }]
  }), [height, chartColors, showLegend, pieData]);

  // Bar/Line/Area/Column/Range/Scatter chart options (must be before finalOptions)
  const commonOptions = useMemo(() => {
    // Map chart types to ApexCharts types
    const getApexChartType = (type) => {
      switch (type) {
        case 'line': return 'line';
        case 'area': return 'area';
        case 'column': return 'column';
        case 'bar': return 'bar';
        case 'range': return 'rangeBar';
        case 'scatter': return 'scatter';
        default: return 'bar';
      }
    };

    // Ensure categories is a valid array
    const validCategories = Array.isArray(categories) && categories.length > 0 
      ? categories 
      : [];

    const baseOptions = {
      chart: {
        type: getApexChartType(chartType),
        height: height,
        stacked: stacked && (chartType === 'bar' || chartType === 'column'),
        toolbar: {
          show: false
        },
        zoom: {
          enabled: chartType === 'scatter' // Enable zoom for scatter charts
        }
      },
      colors: chartColors,
      dataLabels: {
        enabled: false
      },
      xaxis: {
        categories: validCategories,
        title: {
          text: horizontal ? (yAxisLabel || '') : (xAxisLabel || ''),
          style: xAxisLabelStyle || {
            fontSize: '13px',
            fontWeight: 600,
            color: '#303030'
          }
        },
        labels: {
          style: {
            fontSize: `${xAxisTickFontSize}px`,
            fontFamily: 'inherit'
          },
          rotate: horizontal ? 0 : -45,
          rotateAlways: false,
          formatter: horizontal
            ? function (val) {
                if (val === null || val === undefined || isNaN(val)) return '0';
                return Number(val).toLocaleString();
              }
            : undefined,
        }
      },
      yaxis: {
        forceNiceScale: true,
        min: undefined, // Let ApexCharts calculate automatically
        max: undefined, // Let ApexCharts calculate automatically
        title: {
          text: horizontal ? (xAxisLabel || '') : (yAxisLabel || ''),
          style: yAxisLabelStyle || {
            fontSize: '13px',
            fontWeight: 600,
            color: '#303030'
          }
        },
        labels: {
          style: {
            fontSize: `${yAxisTickFontSize}px`,
            fontFamily: 'inherit'
          },
          formatter: function (val) {
            if (horizontal) {
              if (val === null || val === undefined) return '';
              return String(val);
            }
            if (val === null || val === undefined || isNaN(val)) {
              return '0';
            }
            return val.toLocaleString();
          }
        }
      },
      grid: {
        borderColor: gridStroke,
        strokeDashArray: 3,
        xaxis: {
          lines: {
            show: true
          }
        },
        yaxis: {
          lines: {
            show: true
          }
        }
      },
      legend: {
        show: showLegendCondition,
        position: 'bottom',
        horizontalAlign: 'center',
        fontSize: '13px',
        fontFamily: 'inherit',
        fontWeight: 400,
        offsetY: 0,
        itemMargin: {
          horizontal: 10,
          vertical: 5
        }
      },
      tooltip: {
        shared: true,
        intersect: false,
        y: {
          formatter: function (val) {
            return val ? val.toLocaleString() : '0';
          }
        },
        custom: function ({ series, seriesIndex, dataPointIndex, w }) {
          if (!w || !w.globals) {
            return '';
          }
          const category = w.globals.categoryLabels && w.globals.categoryLabels[dataPointIndex] ? w.globals.categoryLabels[dataPointIndex] : '';
          let tooltipContent = `<div style="background-color: #ffffff; border: 1px solid #e8eaed; border-radius: 8px; padding: 12px; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);">`;
          tooltipContent += `<div style="font-weight: 600; color: #005587; font-size: 13px; margin-bottom: 4px;">${category}</div>`;
          
          if (series && Array.isArray(series)) {
            series.forEach((s, idx) => {
              if (s && Array.isArray(s) && s[dataPointIndex] !== undefined) {
                const value = s[dataPointIndex];
                const seriesName = w.globals.seriesNames && w.globals.seriesNames[idx] ? w.globals.seriesNames[idx] : `Series ${idx + 1}`;
                tooltipContent += `<div style="color: #303030; font-size: 13px;">${seriesName}: <strong>${typeof value === 'number' ? value.toLocaleString() : value}</strong></div>`;
              }
            });
          }
          
          tooltipContent += `</div>`;
          return tooltipContent;
        }
      }
    };

    // Add chart-specific options
    if (chartType === 'line' || chartType === 'area') {
      baseOptions.stroke = {
        width: strokeWidth,
        curve: 'smooth'
      };
      baseOptions.markers = {
        size: 4,
        hover: {
          size: 6
        }
      };
      if (chartType === 'area') {
        baseOptions.fill = {
          type: 'gradient',
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.7,
            opacityTo: 0.3,
            stops: [0, 90, 100]
          }
        };
      }
    } else if (chartType === 'bar' || chartType === 'column') {
      baseOptions.plotOptions = {
        bar: {
          borderRadius: horizontal ? 2 : 8,
          horizontal: horizontal,
          columnWidth: horizontal ? undefined : '70%',
          barHeight: horizontal ? '65%' : undefined,
          dataLabels: {
            position: horizontal ? 'center' : 'top'
          }
        }
      };
    } else if (chartType === 'range') {
      baseOptions.plotOptions = {
        bar: {
          borderRadius: 8,
          horizontal: false,
          dataLabels: {
            position: 'top'
          }
        }
      };
    } else if (chartType === 'scatter') {
      baseOptions.stroke = {
        width: 0
      };
      baseOptions.markers = {
        size: 6,
        hover: {
          size: 8
        }
      };
    }

    return baseOptions;
  }, [
    chartType,
    height,
    chartColors,
    categories,
    xAxisLabel,
    xAxisLabelStyle,
    xAxisTickFontSize,
    yAxisLabel,
    yAxisLabelStyle,
    yAxisTickFontSize,
    gridStroke,
    showLegendCondition,
    strokeWidth,
    stacked,
    horizontal
  ]);

  // Final options with normalized categories (always calculate, even if not used)
  const finalOptions = useMemo(() => {
    const options = {
      ...commonOptions,
      xaxis: {
        ...commonOptions.xaxis,
        categories: normalizedCategories
      },
      yaxis: {
        ...commonOptions.yaxis,
        forceNiceScale: true,
        min: undefined, // Let ApexCharts calculate automatically
        max: undefined, // Let ApexCharts calculate automatically
      }
    };

    if (chartType === 'line' && averageLine != null && !Number.isNaN(averageLine)) {
      const avgLabel = averageLine >= 1000
        ? `Avg: ${(averageLine / 1000).toFixed(1)}K`
        : `Avg: ${averageLine.toLocaleString(undefined, { maximumFractionDigits: 1 })}`;

      options.annotations = {
        yaxis: [{
          y: averageLine,
          borderColor: '#666666',
          strokeDashArray: 0,
          label: {
            borderColor: 'transparent',
            style: {
              background: 'transparent',
              color: '#666666',
              fontSize: '12px',
              fontWeight: 500,
            },
            text: avgLabel,
            position: 'right',
            offsetX: -8,
          },
        }],
      };
    }

    // For bar/column charts, ensure plotOptions is properly set
    if (chartType === 'bar' || chartType === 'column') {
      if (!options.plotOptions) {
        options.plotOptions = {};
      }
      options.plotOptions.bar = {
        borderRadius: horizontal ? 2 : 8,
        horizontal: horizontal,
        columnWidth: horizontal ? undefined : '70%',
        barHeight: horizontal ? '65%' : undefined,
        dataLabels: {
          position: horizontal ? 'center' : 'top',
          enabled: false
        },
        distributed: useDistributedBars
      };
      // Ensure yaxis is properly configured for bar/column charts
      if (!options.yaxis) {
        options.yaxis = {};
      }
      options.yaxis.min = undefined; // Let ApexCharts calculate
      options.yaxis.max = undefined; // Let ApexCharts calculate
    }

    return options;
  }, [commonOptions, normalizedCategories, chartType, horizontal, averageLine, useDistributedBars]);

  // Map chart types to ApexCharts types (helper function, not a hook)
  const getApexChartType = (type) => {
    switch (type) {
      case 'line': return 'line';
      case 'area': return 'area';
      case 'column': return 'column';
      case 'bar': return 'bar';
      case 'range': return 'rangeBar';
      case 'scatter': return 'scatter';
      default: return 'bar';
    }
  };

  // Early returns after all hooks
  if (!chartData || chartData.length === 0) {
    return <Typography>No data available for chart</Typography>;
  }

  // Handle pie and radialbar charts (they don't use xaxis/yaxis)
  if (chartType === 'pie' || chartType === 'radialbar') {
    if (chartType === 'radialbar') {
    return (
        <Box>
          <Chart
            key={`radialbar-${chartId}`}
            options={radialBarOptions}
            series={pieSeries}
            type="radialBar"
            height={height}
          />
        </Box>
      );
    }

    return (
      <Box>
        <Chart
          key={`pie-${chartId}`}
          options={pieOptions}
          series={pieSeries}
          type="pie"
          height={height}
        />
      </Box>
    );
  }

  // Ensure series is valid before rendering for other chart types
  if (!memoizedSeries || memoizedSeries.length === 0 || !categories || categories.length === 0) {
    return <Typography>No valid data available for chart</Typography>;
  }

  // Final validation - ensure we have valid data
  if (!normalizedCategories || normalizedCategories.length === 0) {
    return <Typography>No categories available for chart</Typography>;
  }

  if (!finalSeries || finalSeries.length === 0) {
    return <Typography>No series data available for chart</Typography>;
  }

  // Final check - ensure we have at least one valid data point
  const hasValidData = finalSeries.some(s => 
    s && s.data && s.data.some(v => typeof v === 'number' && !isNaN(v))
  );

  if (!hasValidData) {
    return <Typography>No valid numeric data available for chart</Typography>;
  }

  // Final validation: ensure all series data arrays are valid and have the same length
  const validatedFinalSeries = finalSeries.map(s => {
    if (!s || !s.data || !Array.isArray(s.data)) {
      return { name: s?.name || 'Series', data: Array(normalizedCategories.length).fill(0) };
    }
    // Ensure all values are numbers (not null/undefined) and array length matches categories
    const validatedData = Array.from({ length: normalizedCategories.length }, (_, idx) => {
      const value = s.data[idx];
      if (value === null || value === undefined) return 0;
      const numValue = typeof value === 'number' ? value : parseFloat(value);
      return isNaN(numValue) ? 0 : numValue;
    });
    return { ...s, data: validatedData };
  });

  // Double-check that all series have the same length as categories
  const allSeriesValid = validatedFinalSeries.every(s => 
    s && s.data && Array.isArray(s.data) && s.data.length === normalizedCategories.length
  );

  if (!allSeriesValid) {
    return <Typography>Data structure error: series and categories length mismatch</Typography>;
  }

  return (
    <Box>
      <Chart
        key={`${chartType}-${chartId}-${normalizedCategories.length}-${validatedFinalSeries.length}`}
        options={finalOptions}
        series={validatedFinalSeries}
        type={getApexChartType(chartType)}
        height={height}
      />
    </Box>
  );
};

export default ChartRenderer;
